/**
 * State management and validation hooks for WireInterfacesStep
 */
import { useState, useCallback, useMemo } from "react";
import type { SmartAssetData, CompositionLink } from "../../types";
import type { CoreLinkDetail } from "./types";
import { collectUniqueAssets } from "./types";

export interface UseWireStateOptions {
  selectedAssets: SmartAssetData[];
  links: CompositionLink[];
  addLink: (link: Omit<CompositionLink, "id">) => void;
}

export interface UseWireStateReturn {
  // Form state
  sourceAsset: string;
  setSourceAsset: (id: string) => void;
  sourceInterface: string;
  setSourceInterface: (iface: string) => void;
  targetAsset: string;
  setTargetAsset: (id: string) => void;
  targetInterface: string;
  setTargetInterface: (iface: string) => void;
  selectedLinkId: string | null;
  setSelectedLinkId: (id: string | null) => void;
  validationError: string | null;

  // Computed
  isFormValid: boolean;
  getLinkType: () => "signal" | "value";
  getAssetName: (id: string) => string;

  // Actions
  handleAddLink: () => void;
  handleAssetCardClick: (assetId: string) => void;
  getCardBorderClass: (assetId: string) => string;
  clearValidationError: () => void;
}

export function useWireState({
  selectedAssets,
  links,
  addLink,
}: UseWireStateOptions): UseWireStateReturn {
  // Form state
  const [sourceAsset, setSourceAsset] = useState("");
  const [sourceInterface, setSourceInterface] = useState("");
  const [targetAsset, setTargetAsset] = useState("");
  const [targetInterface, setTargetInterface] = useState("");
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Memoized maps
  const assetNameMap = useMemo(
    () => new Map(selectedAssets.map((asset) => [asset.id, asset.name])),
    [selectedAssets],
  );

  // Derive link type from interface selection
  const getLinkType = useCallback((): "signal" | "value" => {
    if (sourceInterface.includes("signal") || targetInterface.includes("signal")) return "signal";
    return "value";
  }, [sourceInterface, targetInterface]);

  // Validate interface compatibility
  const validateInterfaceCompatibility = useCallback(
    (srcIface: string, tgtIface: string): string | null => {
      const srcType = srcIface.replace("_out", "");
      const tgtType = tgtIface.replace("_in", "");
      if (srcType !== tgtType) {
        return `Incompatible interfaces: ${srcIface} cannot connect to ${tgtIface}. Use matching types (signal→signal, value→value, charge→charge).`;
      }
      return null;
    },
    [],
  );

  // Check for duplicate links
  const validateNoDuplicate = useCallback(
    (srcId: string, srcIface: string, tgtId: string, tgtIface: string): string | null => {
      const duplicate = links.find(
        (link) =>
          link.source_id === srcId &&
          link.source_interface === srcIface &&
          link.target_id === tgtId &&
          link.target_interface === tgtIface,
      );
      if (duplicate) {
        return "This connection already exists.";
      }
      return null;
    },
    [links],
  );

  // Check if form is valid (Boolean wrapper ensures true/false, not empty string)
  const isFormValid = Boolean(
    sourceAsset && sourceInterface && targetAsset && targetInterface && sourceAsset !== targetAsset,
  );

  // Handle adding a new link
  const handleAddLink = useCallback(() => {
    setValidationError(null);

    if (!sourceAsset || !targetAsset) {
      setValidationError("Please select both source and target assets.");
      return;
    }
    if (sourceAsset === targetAsset) {
      setValidationError("Source and target must be different assets.");
      return;
    }
    if (!sourceInterface) {
      setValidationError("Please select a source interface.");
      return;
    }
    if (!targetInterface) {
      setValidationError("Please select a target interface.");
      return;
    }

    const compatibilityError = validateInterfaceCompatibility(sourceInterface, targetInterface);
    if (compatibilityError) {
      setValidationError(compatibilityError);
      return;
    }

    const duplicateError = validateNoDuplicate(
      sourceAsset,
      sourceInterface,
      targetAsset,
      targetInterface,
    );
    if (duplicateError) {
      setValidationError(duplicateError);
      return;
    }

    addLink({
      source_id: sourceAsset,
      source_interface: sourceInterface as "signal_out" | "value_out" | "charge_out",
      target_id: targetAsset,
      target_interface: targetInterface as "signal_in" | "value_in" | "charge_in",
      link_type: getLinkType(),
    });

    // Reset form
    setSourceAsset("");
    setSourceInterface("");
    setTargetAsset("");
    setTargetInterface("");
    setValidationError(null);
  }, [
    sourceAsset,
    targetAsset,
    sourceInterface,
    targetInterface,
    validateInterfaceCompatibility,
    validateNoDuplicate,
    addLink,
    getLinkType,
  ]);

  // Get asset name by ID
  const getAssetName = useCallback((id: string) => assetNameMap.get(id) || id, [assetNameMap]);

  // Handle clicking on an asset card
  const handleAssetCardClick = useCallback(
    (assetId: string) => {
      if (!sourceAsset) {
        setSourceAsset(assetId);
      } else if (sourceAsset === assetId) {
        setSourceAsset("");
      } else if (!targetAsset) {
        setTargetAsset(assetId);
      } else if (targetAsset === assetId) {
        setTargetAsset("");
      } else {
        setTargetAsset(assetId);
      }
    },
    [sourceAsset, targetAsset],
  );

  // Get card border style
  const getCardBorderClass = useCallback(
    (assetId: string) => {
      if (sourceAsset === assetId) {
        return "border-2 border-primary ring-2 ring-primary/40";
      }
      if (targetAsset === assetId) {
        return "border-2 border-sa-success ring-2 ring-sa-success/40";
      }
      return "border border-sa-border hover:border-sa-border-hover";
    },
    [sourceAsset, targetAsset],
  );

  const clearValidationError = useCallback(() => setValidationError(null), []);

  return {
    sourceAsset,
    setSourceAsset,
    sourceInterface,
    setSourceInterface,
    targetAsset,
    setTargetAsset,
    targetInterface,
    setTargetInterface,
    selectedLinkId,
    setSelectedLinkId,
    validationError,
    isFormValid,
    getLinkType,
    getAssetName,
    handleAddLink,
    handleAssetCardClick,
    getCardBorderClass,
    clearValidationError,
  };
}

/** Hook for computing canvas data from links */
export function useCanvasData(
  selectedAssets: SmartAssetData[],
  links: CompositionLink[],
  getAssetName: (id: string) => string,
) {
  const assetById = useMemo(
    () => new Map(selectedAssets.map((asset) => [asset.id, asset])),
    [selectedAssets],
  );

  const coreAsset = useMemo(() => {
    if (selectedAssets.length === 0) return undefined;
    return selectedAssets[0];
  }, [selectedAssets]);

  const coreId = coreAsset?.id ?? "";

  const coreLinks = useMemo(() => {
    if (!coreId) return [];
    return links.filter((link) => link.source_id === coreId || link.target_id === coreId);
  }, [links, coreId]);

  const coreLinkDetails = useMemo<CoreLinkDetail[]>(() => {
    return coreLinks.map((link) => {
      const coreIsSource = link.source_id === coreId;
      const externalId = coreIsSource ? link.target_id : link.source_id;
      return {
        link,
        coreIsSource,
        externalId,
        externalAsset: assetById.get(externalId),
        coreInterface: coreIsSource ? link.source_interface : link.target_interface,
        externalInterface: coreIsSource ? link.target_interface : link.source_interface,
        direction: coreIsSource ? "out" : "in",
      };
    });
  }, [coreLinks, coreId, assetById]);

  const signalConnections = useMemo(
    () => coreLinkDetails.filter((item) => item.coreInterface.startsWith("signal")),
    [coreLinkDetails],
  );
  const valueInConnections = useMemo(
    () => coreLinkDetails.filter((item) => item.coreInterface === "value_in"),
    [coreLinkDetails],
  );
  const valueOutConnections = useMemo(
    () => coreLinkDetails.filter((item) => item.coreInterface === "value_out"),
    [coreLinkDetails],
  );
  const chargeConnections = useMemo(
    () => coreLinkDetails.filter((item) => item.coreInterface.startsWith("charge")),
    [coreLinkDetails],
  );

  const valueInAssets = useMemo(
    () => collectUniqueAssets(valueInConnections),
    [valueInConnections],
  );
  const valueOutAssets = useMemo(
    () => collectUniqueAssets(valueOutConnections),
    [valueOutConnections],
  );

  const hasSignal = signalConnections.length > 0;
  const hasValueIn = valueInConnections.length > 0;
  const hasValueOut = valueOutConnections.length > 0;
  const hasCharge = chargeConnections.length > 0;

  const activityItems = useMemo(() => {
    return links.slice(-3).map((link, index) => {
      const time = `14:30:${String(20 + index).padStart(2, "0")}`;
      return {
        id: link.id,
        time,
        message: `${getAssetName(link.source_id)} → ${getAssetName(link.target_id)} (${link.link_type})`,
      };
    });
  }, [links, getAssetName]);

  return {
    coreAsset,
    coreId,
    signalConnections,
    valueInConnections,
    valueOutConnections,
    chargeConnections,
    valueInAssets,
    valueOutAssets,
    hasSignal,
    hasValueIn,
    hasValueOut,
    hasCharge,
    activityItems,
  };
}
